import React, { useState, useEffect, useContext } from 'react';
import styles from './index.module.scss';
import HeadContainer from '../../../common/head-container';
import { getImageFromS3 } from '../../../imageService';
import apiService from '../../../utils/apiService';
import classNames from 'classnames';
import { DataLayerEventContext } from '../../../contexts/dataLayerEventContext';

const OtpScreen = ({
  setScreenNumber,
  handleChangePhoneNumber,
  successSendOtp,
  telegramLink,
  onTelegramHandleClick
}) => {
  const phoneNumber = sessionStorage.getItem('p_n');
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResendAvailable, setIsResendAvailable] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendEvent } = useContext(DataLayerEventContext);

  useEffect(() => {
    window.scrollTo(0, 0);
    sendEvent('seen_authorization');
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsResendAvailable(true);
    }
  }, [timeLeft]);

  const handleResend = async () => {
    if (resendCount <= 3) {
      setResendCount(resendCount + 1);
      const userId = sessionStorage.getItem('u_i');
      await apiService.post(`api/cda/users/${userId}/verification`);
      setTimeLeft(60);
      setIsResendAvailable(false);
      setOtp(new Array(6).fill(''));
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    if (value.length === 6) {
      const fullCode = value.split('').slice(0, 6);
      setOtp(fullCode);
      return;
    }

    if (!isNaN(value) && value.length === 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (index < 5) {
        const nextInput = e.target.nextSibling;
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);

      if (e.target.previousSibling) e.target.previousSibling.focus();
    } else if (e.key >= '0' && e.key <= '9') {
      if (otp[index] !== '' && index < 5) {
        e.preventDefault();

        const newOtp = [...otp];
        newOtp[index + 1] = e.key;
        setOtp(newOtp);

        const nextInput = e.target.nextSibling;
        if (nextInput) nextInput.focus();
      }
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const userId = sessionStorage.getItem('u_i');
      await apiService.post(`api/cda/users/${userId}/verification/validate`, {
        data: {
          code: otp.join('')
        }
      });
      successSendOtp();
    } catch (e) {
      const code = e?.response?.data?.code;

      if (code === 100004) setScreenNumber(500);

      if (code === 100003) successSendOtp();

      if (otp.join('')?.length < 5 || code === 100006) setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if ('OTPCredential' in window) {
      const abortController = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: abortController.signal
        })
        .then((otpCredential) => {
          if (otpCredential && otpCredential.code) {
            const codeArray = otpCredential.code.split('').slice(0, 6);
            setOtp(codeArray);
            document
              .querySelectorAll('input[type="number"]')
              .forEach((input, idx) => {
                input.value = codeArray[idx] || '';
              });
          }
        })
        .catch((err) => {
          console.error('OTP API Error:', err);
        });

      return () => abortController.abort();
    }
  }, []);

  return (
    <div
      className={styles.container}
      style={{
        backgroundImage: `url("${getImageFromS3('images/ua-hostess/bg.png')}")`
      }}
    >
      <HeadContainer
        setScreenNumber={setScreenNumber}
        telegramOnly
        customTelegramLink={telegramLink}
        onTelegramHandleClick={onTelegramHandleClick}
      />
      <div className={styles.content}>
        <p className={styles.title}>
          {`Ми надіслали повідомлення на номер 380*******${
            phoneNumber ? phoneNumber?.slice(-2) : '**'
          }`}
        </p>
        <p className={styles.description}>
          Для підтвердження номеру введи код із SMS
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className={styles.otpContainer}>
            {otp.map((data, index) => (
              <input
                key={index}
                type="number"
                maxLength="1"
                value={data}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onChange={(e) => handleChange(e, index)}
                onFocus={() => {
                  setError(false);
                }}
                className={classNames(error && styles.errorInput)}
                autoComplete="one-time-code"
              />
            ))}
          </div>
        </form>
        {resendCount < 3 &&
          (isResendAvailable ? (
            <button className={styles.resendButton} onClick={handleResend}>
              Повторно отримати код
            </button>
          ) : (
            <div className={styles.timer}>
              Отримати код повторно через{' '}
              {String(Math.floor(timeLeft / 60)).padStart(2, '0')}:
              {String(timeLeft % 60).padStart(2, '0')}
            </div>
          ))}
        <div className={styles.buttonContainer}>
          {error && (
            <p className={styles.error}>Перевір коректність введеного коду</p>
          )}
          <button
            className={styles.sendButton}
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting && <span className={styles.spinner} />}
            <span>Надіслати</span>
          </button>
          <button
            className={styles.changeNumberButton}
            onClick={handleChangePhoneNumber}
          >
            Змінити номер
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtpScreen;
